import React, { useState, useContext, useEffect, useMemo } from "react";
import apiClient from "../../ApiEndpoint/apiClient";
import apiUrl from "../../ApiEndpoint/config";
import { Tabs, Collapse, Spin } from "antd";
import { RegisterIdContext } from "../../hooks/RegisterIdContext";


const { TabPane } = Tabs;
const { Panel } = Collapse;


const PlantsDetails = () => {
    const { plantObject } = useContext(RegisterIdContext);

    const [message, setMessage] = useState("");
    const [citations, setCitations] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!Array.isArray(plantObject) || plantObject.length === 0) {
            setMessage("");
            setCitations([]);
            return;
        }

        const fetchFacilitySustainability = async () => {
            setLoading(true);
            try {
                const plant = plantObject[0]; // assuming single selection

                const params = {
                    plant_id: plant.plantId,
                    plant_name: plant.plantName,
                    group: plant.operator?.companyName,
                    plant_state: plant.physicalAddress?.stateName,
                    plant_city: plant.physicalAddress?.city,
                };

                const queryString = new URLSearchParams(params).toString();

                const res = await apiClient.get(
                    `${apiUrl}/facility_sustainability?${queryString}`
                );

                setMessage(res?.data?.Message || "");
                setCitations(res?.data?.Citations || []);
            } catch (err) {
                console.error("Error fetching facility data:", err);
                setMessage("An error occurred. Please contact the Administrator.");
                setCitations([]);
            } finally {
                setLoading(false);
            }
        };

        fetchFacilitySustainability();
    }, [plantObject]);


    return (
        <>
            <div className="card" style={{ padding: '10px' }}>
                <div className="mt-2">
                    <Tabs defaultActiveKey="1">
                        <TabPane tab="Plants Details" key="1">
                            <Plants />
                        </TabPane>
                        <TabPane tab="Sustainability Goals " key="2">
                            <SustainabilityGoals message={message}
                                citations={citations}
                                loading={loading} />
                        </TabPane>
                        <TabPane tab="Contacts" key="3">
                            <Contacts />
                        </TabPane>

                    </Tabs>
                </div>
            </div>
        </>

    );
};




const Plants = () => {
    return (
        <Tabs defaultActiveKey="1">
            <TabPane tab="Overview" key="1">
                <Overview />
            </TabPane>
        </Tabs>
    )
}



const Overview = () => {
    const { plantObject } = useContext(RegisterIdContext); // Import projectId and setProjectId from context
    const selectedPlant = plantObject[0];


    //Facility Sumary
    const [message, setMessage] = useState("");
    const [citations, setCitations] = useState([]);
    const [loadingSummary, setLoadingSummary] = useState(true);

    useEffect(() => {
        if (!Array.isArray(plantObject) || plantObject.length === 0) {
            setMessage("");
            setCitations([]);
            setLoadingSummary(false);
            return;
        }

        const fetchSummary = async () => {
            setLoadingSummary(true);
            try {
                const params = {
                    plant_id: selectedPlant?.plantId,
                    plant_name: selectedPlant?.plantName,
                    group_name: selectedPlant?.operator?.companyName,
                    plant_state: selectedPlant?.physicalAddress?.stateName,
                    plant_city: selectedPlant?.physicalAddress?.city,
                };

                const queryString = new URLSearchParams(params).toString();

                const res = await apiClient.get(`${apiUrl}/facility_summary?${queryString}`);

                setMessage(res.data?.Message || "");
                setCitations(res.data?.Citations || []);
            } catch (err) {
                console.error("Error fetching facility sustainability:", err);
                setMessage("");
                setCitations([]);
            } finally {
                setLoadingSummary(false);
            }
        };

        fetchSummary();
    }, [plantObject])




    //format plant date 
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const cleaned = dateString.replace(/\[.*\]/, ""); // remove [UTC] or similar
        const date = new Date(cleaned);
        return date.toLocaleDateString("en-US");
    };
    //format plant address
    const getFormattedMailingAddress = (address) =>
        [
            address?.addressLine1,
            address?.city,
            address?.stateName,
            address?.postalCode,
            address?.countyName,
        ]
            .filter(Boolean)
            .join(", ") || "N/A";

    //format plant phonenumber
    const formatPhoneNumber = (phone) =>
        !phone || !phone.number || phone.cc === 0 ? "N/A" : `+${phone.cc} ${phone.number}`;

    //MailingAddress mapped here from the selectedPlant
    const mailingAddress = selectedPlant?.mailingAddress || {};

    return (
        <>
            <div style={{ maxHeight: "70vh", overflowY: "scroll" }}>
                <h5 className="site-title mb-2">
                    {selectedPlant?.plantName ?? "N/A"}
                </h5>

                {/* 🌟 Facility Summary Section */}

                <div>
                    {loadingSummary ? (
                        <p className="blinking-text">Loading  summary...</p>
                    ) : (
                        (message || citations.length > 0) && (
                            <div className="facility-summary-content">
                                {message && <p style={{ lineHeight: "1.6" }}>{message}</p>}

                                {citations.length > 0 && (
                                    <ul>
                                        {citations.map((citation, index) => {
                                            if (typeof citation !== "string") return null;
                                            const match = citation.match(/\[([^\]]+)\]\(([^)]+)\)/);

                                            if (match) {
                                                const [, label, url] = match;
                                                return (
                                                    <li key={index}>
                                                        <a href={url} target="_blank" rel="noopener noreferrer">
                                                            {label}
                                                        </a>
                                                    </li>
                                                );
                                            }

                                            return (
                                                <li key={index}>
                                                    <a href={citation} target="_blank" rel="noopener noreferrer">
                                                        {citation}
                                                    </a>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        )
                    )}
                </div>

                <div className="opportunity">
                    <h6 style={{ fontSize: "18px", paddingBottom: "6px" }}>Plant Details</h6>
                    <div className="detection-item">
                        <p>
                            <span className="left">Plant Name:</span>
                            <span className="right">{selectedPlant?.plantName ?? "N/A"}</span>
                        </p>

                        <p>
                            <span className="left">Number of employees:</span>
                            <span className="right">{selectedPlant?.noEmployees ?? "N/A"}</span>
                        </p>
                        <p>
                            <span className="left">Startup date:</span>
                            <span className="right">{formatDate(selectedPlant?.startupDate)}</span>
                        </p>
                        <p>
                            <span className="left">Close date:</span>
                            <span className="right">{formatDate(selectedPlant?.closeDate)}</span>
                        </p>
                        <p>
                            <span className="left">Existing Sq.Ft.:</span>
                            <span className="right">
                                {selectedPlant?.existingSqFt
                                    ? `${Number(selectedPlant.existingSqFt).toLocaleString("en-US")} sq ft`
                                    : "N/A"}
                            </span>
                        </p>
                    </div>

                    {/* Mailing Address */}
                    <div className="detection-item">
                        <h6 style={{ fontSize: "18px" }}>Mailing Address</h6>
                        <p>
                            <span className="left" style={{ width: "10rem" }}>Address:</span>
                            <span className="right">{getFormattedMailingAddress(mailingAddress)}</span>
                        </p>
                        <p>
                            <span className="left">Plant Phone:</span>
                            <span className="right">{formatPhoneNumber(selectedPlant?.phone)}</span>
                        </p>
                    </div>

                    {/* Industry Sector */}
                    <div className="detection-item">
                        <h6 style={{ fontSize: "18px" }}>Plant Industry Sector</h6>
                        {selectedPlant?.sectors?.length > 0 ? (
                            selectedPlant.sectors.map((sector, index) => (
                                <p key={sector.plantSectorDesc || index}>
                                    <span className="left">{sector.plantSectorDesc ?? "N/A"}</span>
                                </p>
                            ))
                        ) : (
                            <p><span className="left">N/A</span></p>
                        )}
                    </div>

                    {/* Fuel */}
                    <div className="detection-item">
                        <h6 style={{ fontSize: "18px" }}>Fuel</h6>
                        <p>
                            <span className="left">Primary Fuel:</span>
                            <span className="right">{selectedPlant?.fuel?.primaryFuel || "N/A"}</span>
                        </p>
                        <p>
                            <span className="left">Secondary Fuel:</span>
                            <span className="right">{selectedPlant?.fuel?.secondaryFuel || "N/A"}</span>
                        </p>
                    </div>

                    {/* Cogen/CHP */}
                    <div className="detection-item">
                        <p>
                            <span style={{ fontSize: "18px", color: "black", fontWeight: "800" }}>Cogen/CHP:</span>
                            <span className="right">{selectedPlant?.cogenChp || "0"}</span>
                        </p>
                    </div>

                    {/* SIC Product */}
                    <div className="detection-item">
                        <h6 style={{ fontSize: "18px" }}>SIC Product</h6>
                        {selectedPlant?.sicProduct?.length > 0 ? (
                            selectedPlant.sicProduct.map((sector, index) => (
                                <p key={sector.sicProductId || index}>
                                    <span className="left">{sector.sicProductDesc ?? "N/A"}</span>
                                </p>
                            ))
                        ) : (
                            <p><span className="left">N/A</span></p>
                        )}
                    </div>

                    {/* OWNERS */}
                    <div className="opportunity-container">
                        {selectedPlant?.owner?.map((owner, index) => (
                            <div key={owner.companyId || index}>
                                <h2 className="opp-main-heading">OWNERS</h2>
                                <div className="opportunity">
                                    <div className="opportunity-name">{owner.companyName ?? "N/A"}</div>
                                    <ul className="opportunity-details">
                                        <li><strong>Classification:</strong> {owner.classificationDesc ?? "N/A"}</li>
                                        <li><strong>Ownership percentage:</strong> {owner.ownerPercent != null ? `${owner.ownerPercent}%` : "N/A"}</li>
                                        <li>
                                            <strong>Website:</strong>{" "}
                                            {owner.companyWebsite ? (
                                                <a href={owner.companyWebsite} target="_blank" rel="noopener noreferrer">
                                                    {owner.companyWebsite}
                                                </a>
                                            ) : "N/A"}
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        ))}

                        {/* PARENT */}
                        {selectedPlant?.parent && (
                            <div>
                                <h2 className="opp-main-heading">PARENT</h2>
                                <div className="opportunity">
                                    <div className="opportunity-name">{selectedPlant.parent.companyName ?? "N/A"}</div>
                                    <ul className="opportunity-details">
                                        <li>
                                            <strong>Website:</strong>{" "}
                                            {selectedPlant.parent.website ? (
                                                <a
                                                    href={
                                                        selectedPlant.parent.website.startsWith("http")
                                                            ? selectedPlant.parent.website
                                                            : `https://${selectedPlant.parent.website}`
                                                    }
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {selectedPlant.parent.website}
                                                </a>
                                            ) : "N/A"}
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        )}

                        {/* CAPACITY */}
                        {selectedPlant?.capacity?.map((capacity, index) => (
                            <div key={capacity.productDesc || index}>
                                <h2 className="opp-main-heading">Capacity</h2>
                                <div className="opportunity">
                                    <div className="opportunity-name">{capacity.productDesc ?? "N/A"}</div>
                                    <ul className="opportunity-details">
                                        <li><strong>Type:</strong> {capacity.type ?? "N/A"}</li>
                                        <li><strong>Actual Capacity:</strong> {capacity.actualCapacity ?? "N/A"}</li>
                                        <li><strong>Design Capacity:</strong> {capacity.designCapacity ?? "N/A"}</li>
                                        <li><strong>Unit of Measures:</strong> {capacity.uom ?? "N/A"}</li>
                                    </ul>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </>


    )
}



const Contacts = () => {
    const { plantObject } = useContext(RegisterIdContext); // Import projectId and setProjectId from context
    const selectedPlant = plantObject[0];
    return (
        <>
            {(selectedPlant?.contact?.length > 0 || selectedPlant?.ltsa?.length > 0) && (
                <div className="card" style={{ marginBottom: "16px", maxHeight: "70vh", overflowY: "scroll" }}>

                    {/* IIR Contact */}
                    {selectedPlant?.contact?.length > 0 && (
                        <div className="opportunity-container">
                            <h2 className="opp-main-heading">IIR Plant Contact</h2>
                            {selectedPlant.contact.map((contact, index) => (
                                <div className="opportunity" key={index}>
                                    <div className="opportunity-name">
                                        {contact.firstName} {contact.lastName}
                                    </div>
                                    <ul className="opportunity-details">
                                        <li><strong>Title:</strong> {contact.actualTitle ?? "N/A"}</li>
                                        <li><strong>Functional Title:</strong> {contact.functionalTitle ?? "N/A"}</li>
                                        <li><strong>On-site:</strong> {contact.onSite ?? "N/A"}</li>
                                        <li>
                                            <strong>Email:</strong>{" "}
                                            {contact.emailAddress ? (
                                                <span style={{ color: "#277bff" }}>{contact.emailAddress}</span>
                                            ) : "N/A"}
                                        </li>
                                        <li>
                                            <strong>LinkedIn:</strong>{" "}
                                            {contact.linkedInId ? (
                                                <a
                                                    href={`https://www.linkedin.com/in/${contact.linkedInId}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {contact.linkedInId}
                                                </a>
                                            ) : "N/A"}
                                        </li>
                                        <li><strong>Phone Number:</strong> {contact.phone?.number ?? "N/A"}</li>
                                        <li><strong>Ext:</strong> {contact.phone?.ext ?? "N/A"}</li>
                                        <li><strong>Country Code:</strong> {contact.phone?.cc ? `+${contact.phone.cc}` : "N/A"}</li>
                                        <li><strong>Alt Phone:</strong> {contact.altPhone?.number ?? "N/A"}</li>
                                        <li><strong>Alt Ext:</strong> {contact.altPhone?.ext ?? "N/A"}</li>
                                        <li><strong>Alt Country Code:</strong> {contact.altPhone?.cc ? `+${contact.altPhone.cc}` : "N/A"}</li>
                                        <li>
                                            <strong>Alt Phone Number:</strong>{" "}
                                            {contact?.altPhoneMobile && contact.altPhoneMobile !== "0"
                                                ? contact.altPhoneMobile
                                                : "N/A"}
                                        </li>
                                    </ul>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* LTSA Contact */}
                    {selectedPlant?.ltsa?.length > 0 && (
                        <div className="opportunity-container">
                            <h2 className="opp-main-heading">Long Term Service Agreement Contact</h2>
                            {selectedPlant.ltsa.map((item, index) => (
                                <div className="opportunity" key={index}>
                                    <div className="opportunity-name">{item.contractorName ?? "Contractor Name"}</div>
                                    <ul className="opportunity-details">
                                        <li><strong>LTSA ID:</strong> {item.ltsaId ?? "N/A"}</li>
                                        <li><strong>Status Description:</strong> {item.statusDesc ?? "N/A"}</li>
                                        <li><strong>Contract Length:</strong> {item.contractLength ?? "N/A"} {item.contractLength ? "years" : ""}</li>
                                        <li>
                                            <strong>Contract Expiration Date:</strong>{" "}
                                            {item.contractExpiration
                                                ? new Date(item.contractExpiration.replace(/\[.*\]/, "")).toLocaleDateString("en-US")
                                                : "N/A"}
                                        </li>
                                        <li><strong>Average Number of Personnel:</strong> {item.personnelCountAvg ?? "N/A"}</li>
                                        <li><strong>Peak Number of Personnel:</strong> {item.personnelCountPeak ?? "N/A"}</li>
                                        <li><strong>LTSA Preferred Contractor:</strong> {item.preferredContractor === "1" ? "Yes" : "No"}</li>
                                        <li>
                                            <strong>QC Date:</strong>{" "}
                                            {item.qcDate
                                                ? new Date(item.qcDate.replace(/\[.*\]/, "")).toLocaleDateString("en-US")
                                                : "N/A"}
                                        </li>
                                    </ul>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </>
    )
}


const SustainabilityGoals = ({ message, citations, loading }) => {

    return (
        <div>
            {loading ? (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "200px", // adjust height as per your UI
                    }}
                >
                    <Spin tip="Loading..." />
                </div>
            ) : (
                <>
                    {message && <p>{message}</p>}
                    {citations.length > 0 && (
                        <ul>
                            {citations.map((citation, index) => {
                                if (typeof citation !== "string") return null;
                                const match = citation.match(/\[([^\]]+)\]\(([^)]+)\)/);

                                if (match) {
                                    const [, label, url] = match;
                                    return (
                                        <li key={index}>
                                            <a href={url} target="_blank" rel="noopener noreferrer">
                                                {label}
                                            </a>
                                        </li>
                                    );
                                }

                                return (
                                    <li key={index}>
                                        <a href={citation} target="_blank" rel="noopener noreferrer">
                                            {citation}
                                        </a>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </>
            )}
        </div>
    );
};
export default PlantsDetails;